import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import ReactFlow, {
  Node,
  Connection,
  useNodesState,
  useEdgesState,
  addEdge,
  Background,
  Controls,
  MiniMap,
  Panel,
  ReactFlowProvider,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import { Box, Button, Alert, Snackbar, IconButton, Tooltip } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import CustomNode from "./CustomNode";
import NodePalette from "./NodePalette";
import PropertiesPanel from "./PropertiesPanel";
import SequenceEdge from "./SequenceEdge";
import {
  ControlGraph,
  NodeType,
  ControlNode,
} from "./types";
import {
  controlGraphToReactFlow,
  reactFlowToControlGraph,
  createDefaultNode,
  generateNodeId,
  validateControlGraph,
  applyTreeLayout,
  getAllVariables,
  findVariableUsages,
  findVariableDeclaration,
} from "./utils";

interface ControlGraphEditorProps {
  controlGraph: ControlGraph | null;
  onSave?: (controlGraph: ControlGraph) => void;
  onClose: () => void;
  readOnly?: boolean;
}

/**
 * Main control graph editor component
 */
const ControlGraphEditorInner: React.FC<ControlGraphEditorProps> = ({
  controlGraph,
  onSave,
  onClose,
  readOnly = false,
}) => {
  const reactFlowInstance = useReactFlow();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  // Convert backend graph to React Flow format and apply tree layout
  const initialData = useMemo(() => {
    const data = controlGraphToReactFlow(controlGraph);
    // Apply tree layout automatically on load
    if (data.nodes.length > 0) {
      data.nodes = applyTreeLayout(data.nodes, data.edges);
    }
    return data;
  }, [controlGraph]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialData.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialData.edges);
  const [selectedNode, setSelectedNode] = useState<ControlNode | null>(null);
  const [rootNodeId, setRootNodeId] = useState<string>(
    controlGraph?.root || ""
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [highlightedVariable, setHighlightedVariable] = useState<string | null>(null);

  // Compute available variables from the graph
  const availableVariables = useMemo(() => getAllVariables(nodes), [nodes]);

  // Handle variable click to highlight related nodes
  const handleVariableClick = useCallback(
    (varName: string) => {
      // Toggle highlight if clicking the same variable
      if (highlightedVariable === varName) {
        setHighlightedVariable(null);
      } else {
        setHighlightedVariable(varName);
      }
    },
    [highlightedVariable]
  );

  // Enrich nodes with highlighting information and variable click handler
  const enrichedNodes = useMemo(() => {
    if (!highlightedVariable) {
      return nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          highlightedVariables: [],
          onVariableClick: handleVariableClick,
        },
      }));
    }

    // Find which nodes should be highlighted
    const usageNodeIds = findVariableUsages(nodes, highlightedVariable);
    const declarationNodeId = findVariableDeclaration(nodes, highlightedVariable);
    const highlightNodeIds = [...usageNodeIds];
    if (declarationNodeId) {
      highlightNodeIds.push(declarationNodeId);
    }

    return nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        highlightedVariables: highlightNodeIds.includes(node.id) ? [highlightedVariable] : [],
        onVariableClick: handleVariableClick,
      },
    }));
  }, [nodes, highlightedVariable, handleVariableClick]);

  // Delete node callback
  const onDeleteNode = useCallback(
    (nodeId: string) => {
      // Remove node
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));

      // Remove edges connected to this node
      setEdges((eds) =>
        eds.filter((e) => e.source !== nodeId && e.target !== nodeId)
      );

      // Update parent-child relationships
      setNodes((nds) =>
        nds.map((node) => {
          if (node.data.children?.includes(nodeId)) {
            return {
              ...node,
              data: {
                ...node.data,
                children: node.data.children.filter((c: string) => c !== nodeId),
              },
            };
          }
          if (node.data.parent === nodeId) {
            return {
              ...node,
              data: { ...node.data, parent: null },
            };
          }
          return node;
        })
      );

      // Clear selection
      setSelectedNode(null);

      // Clear root if deleting root
      if (rootNodeId === nodeId) {
        setRootNodeId("");
      }
    },
    [setNodes, setEdges, rootNodeId]
  );

  // Format layout callback
  const handleFormatLayout = useCallback(() => {
    const layoutedNodes = applyTreeLayout(nodes, edges);
    setNodes(layoutedNodes);
    setSuccessMessage("Layout formatted!");

    // Fit view after layout
    setTimeout(() => {
      reactFlowInstance.fitView({ padding: 0.2, duration: 400 });
    }, 50);
  }, [nodes, edges, setNodes, reactFlowInstance]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if typing in input
      if ((event.target as HTMLElement).tagName === "INPUT" ||
          (event.target as HTMLElement).tagName === "TEXTAREA") {
        return;
      }

      // Delete key - delete selected node
      if ((event.key === "Delete" || event.key === "Backspace") && selectedNode && !readOnly) {
        event.preventDefault();
        onDeleteNode(selectedNode.id);
      }

      // F key - format layout
      if (event.key === "f" || event.key === "F") {
        event.preventDefault();
        handleFormatLayout();
      }

      // Escape key - deselect
      if (event.key === "Escape") {
        setSelectedNode(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedNode, readOnly, onDeleteNode, handleFormatLayout]);

  // Define custom node types
  const nodeTypes = useMemo(() => ({ customNode: CustomNode }), []);

  // Define custom edge types
  const edgeTypes = useMemo(() => ({ sequence: SequenceEdge }), []);

  // Handle node selection
  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNode(node.data as ControlNode);
  }, []);

  // Handle pane click to deselect
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // Handle new connections (edges)
  const onConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;

      setEdges((eds) => addEdge({ ...connection, type: "smoothstep" }, eds));

      // Update parent-child relationships in node data
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === connection.source) {
            const children = [...(node.data.children || [])];
            if (!children.includes(connection.target!)) {
              children.push(connection.target!);
            }
            return {
              ...node,
              data: { ...node.data, children },
            };
          }
          if (node.id === connection.target) {
            return {
              ...node,
              data: { ...node.data, parent: connection.source },
            };
          }
          return node;
        })
      );
    },
    [setEdges, setNodes]
  );

  // Handle drag-and-drop from palette
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      if (!reactFlowWrapper.current || !reactFlowInstance) return;

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const nodeTypeStr = event.dataTransfer.getData("application/reactflow");

      if (!nodeTypeStr) return;

      const nodeType = nodeTypeStr as NodeType;
      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNodeId = generateNodeId(nodeType);
      const newNodeData = createDefaultNode(nodeType, newNodeId, position);

      const newNode: Node = {
        id: newNodeId,
        type: "customNode",
        position,
        data: newNodeData,
      };

      setNodes((nds) => nds.concat(newNode));

      // Set as root if it's the first node
      if (nodes.length === 0) {
        setRootNodeId(newNodeId);
      }
    },
    [reactFlowInstance, setNodes, nodes.length]
  );

  const onDragStart = (event: React.DragEvent, nodeType: NodeType) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  // Update node properties
  const onUpdateNode = useCallback(
    (nodeId: string, updates: Partial<ControlNode>) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId) {
            const updatedData = { ...node.data, ...updates };
            return { ...node, data: updatedData };
          }
          return node;
        })
      );

      // Update selected node
      if (selectedNode?.id === nodeId) {
        setSelectedNode((prev) => (prev ? { ...prev, ...updates } as ControlNode : null));
      }
    },
    [setNodes, selectedNode]
  );

  // Save control graph
  const handleSave = useCallback(() => {
    if (!onSave) return;

    if (!rootNodeId) {
      setErrors(["Please set a root node before saving"]);
      return;
    }

    // Apply tree layout before saving to ensure nice formatting
    const layoutedNodes = applyTreeLayout(nodes, edges);
    const graph = reactFlowToControlGraph(layoutedNodes, edges, rootNodeId);
    const validationErrors = validateControlGraph(graph);

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    onSave(graph);
    setSuccessMessage("Control graph saved successfully!");
  }, [nodes, edges, rootNodeId, onSave]);

  // Set root node
  const handleSetRoot = useCallback(() => {
    if (selectedNode) {
      setRootNodeId(selectedNode.id);
      setSuccessMessage(`Set as root node`);
    }
  }, [selectedNode]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "85vh",
        width: "100%",
        border: "2px solid #E5E7EB",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "#F8F9FA",
      }}
    >
      {/* Node Palette - Hidden in read-only mode */}
      {!readOnly && (
        <Box
          sx={{
            borderRight: "2px solid #E5E7EB",
            backgroundColor: "#FAFAFA",
          }}
        >
          <NodePalette onDragStart={onDragStart} />
        </Box>
      )}

      {/* Main Canvas */}
      <Box ref={reactFlowWrapper} sx={{ flex: 1, position: "relative" }}>
        <ReactFlow
          nodes={enrichedNodes}
          edges={edges}
          onNodesChange={readOnly ? undefined : onNodesChange}
          onEdgesChange={readOnly ? undefined : onEdgesChange}
          onConnect={readOnly ? undefined : onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onDrop={readOnly ? undefined : onDrop}
          onDragOver={readOnly ? undefined : onDragOver}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          nodesDraggable={!readOnly}
          nodesConnectable={!readOnly}
          elementsSelectable={true}
        >
          <Background />
          <Controls />
          <MiniMap
            nodeColor={(node) => {
              const nodeData = node.data as any;
              return nodeData.color || "#6B7280";
            }}
          />

          {/* Top Panel with Actions */}
          <Panel position="top-right">
            <Box sx={{ display: "flex", gap: 0.75, backgroundColor: "white", p: 0.75, borderRadius: 1, boxShadow: 2 }}>
              <Tooltip title="Auto-arrange nodes (Shortcut: F)">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AccountTreeIcon />}
                  onClick={handleFormatLayout}
                  sx={{ fontSize: "0.75rem" }}
                >
                  Format
                </Button>
              </Tooltip>

              <Tooltip title="Fit all nodes in view">
                <IconButton
                  size="small"
                  onClick={() => reactFlowInstance.fitView({ padding: 0.2, duration: 400 })}
                >
                  <FitScreenIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Box sx={{ fontWeight: 600, mb: 0.5 }}>Keyboard Shortcuts:</Box>
                    <Box sx={{ fontSize: "0.75rem" }}>
                      <div>• <strong>F</strong> - Format layout</div>
                      <div>• <strong>Delete/Backspace</strong> - Delete selected node</div>
                      <div>• <strong>Esc</strong> - Deselect node</div>
                      <div>• <strong>Scroll</strong> - Zoom in/out</div>
                      <div>• <strong>Click + Drag</strong> - Pan canvas</div>
                    </Box>
                  </Box>
                }
              >
                <IconButton size="small">
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {!readOnly && selectedNode && (
                <Button
                  size="small"
                  variant={rootNodeId === selectedNode.id ? "contained" : "outlined"}
                  onClick={handleSetRoot}
                  disabled={rootNodeId === selectedNode.id}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {rootNodeId === selectedNode.id ? "Root" : "Set Root"}
                </Button>
              )}

              {!readOnly && onSave && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  sx={{ fontSize: "0.75rem" }}
                >
                  Save
                </Button>
              )}

              <Button
                size="small"
                variant="outlined"
                onClick={onClose}
                sx={{ fontSize: "0.75rem" }}
              >
                Close
              </Button>
            </Box>
          </Panel>

          {/* Root indicator */}
          {rootNodeId && (
            <Panel position="top-left">
              <Box sx={{ backgroundColor: "white", p: 1, borderRadius: 1, boxShadow: 1 }}>
                <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>Root Node</Box>
                <Box sx={{ fontWeight: 600, fontSize: "0.875rem" }}>{rootNodeId}</Box>
              </Box>
            </Panel>
          )}
        </ReactFlow>
      </Box>

      {/* Properties Panel */}
      <Box
        sx={{
          borderLeft: "2px solid #E5E7EB",
          backgroundColor: "#FAFAFA",
        }}
      >
        <PropertiesPanel
          selectedNode={selectedNode}
          allNodes={nodes}
          onUpdateNode={readOnly ? () => {} : onUpdateNode}
          onDeleteNode={readOnly ? () => {} : onDeleteNode}
          readOnly={readOnly}
          availableVariables={availableVariables}
        />
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={errors.length > 0}
        autoHideDuration={6000}
        onClose={() => setErrors([])}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrors([])}>
          {errors.map((err, i) => (
            <div key={i}>{err}</div>
          ))}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/**
 * Wrapper with ReactFlowProvider
 */
const ControlGraphEditor: React.FC<ControlGraphEditorProps> = (props) => {
  return (
    <ReactFlowProvider>
      <ControlGraphEditorInner {...props} />
    </ReactFlowProvider>
  );
};

export default ControlGraphEditor;
